// masterformPrint.js — «محضر مناقشة بحث لنيل شهادة الماستر», ported 1:1 from the old
// masterform/simple_pdf.php. Opens a print-ready page in a new tab and starts printing
// (the admin picks a printer or "Save as PDF").

const esc = (v) => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

// d/m/Y like the PHP version; empty for missing dates
const fmt = (d) => {
  if (!d) return '';
  const [y, m, day] = String(d).slice(0, 10).split('-');
  return y && m && day ? `${day}/${m}/${y}` : '';
};

const STYLE = `
  @page { margin: 15mm 18mm; size: A4; }
  body { font-family: Arial, sans-serif; font-size: 14px; line-height: 1.5; color: #000; margin: 0; padding: 0; direction: rtl; text-align: right; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 15px; border-bottom: 1px solid #000; padding-bottom: 8px; width: 100%; }
  .logo-container { flex: 0 0 100px; text-align: left; }
  .logo-container img, .right-logo-container img { width: 200px; height: 80px; object-fit: contain; }
  .right-logo-container { flex: 0 0 100px; text-align: right; }
  .title { text-align: center; font-size: 18px; font-weight: bold; margin: 18px 0; text-decoration: underline; }
  .info-section { margin: 15px 0; font-size: 14px; }
  .info-line { margin: 8px 0; display: flex; align-items: center; }
  .label { margin-left: 10px; font-weight: normal; white-space: nowrap; }
  .value { border-bottom: 1px dotted #000; min-width: 200px; padding: 0 5px; margin: 0 5px; }
  .value2 { min-width: 200px; padding: 0 5px; margin: 0 5px; }
  .empty-field { border-bottom: 1px dotted #000; min-width: 200px; padding: 0 5px; margin: 0 5px; height: 20px; }
  .signature-box { float: left; width: 140px; text-align: center; margin: 0 0 10px 10px; }
  .signature-box .signature-title { font-weight: bold; font-size: 13px; margin-bottom: 8px; }
  .signature-box .signature-frame { border: 1px solid #000; width: 140px; height: 80px; }
  .research-title { clear: left; margin: 15px 0; text-align: center; }
  .title-line { border-bottom: 1px dotted #000; min-height: 25px; margin: 5px 0; padding: 5px; }
  .committee-section { margin: 15px 0; }
  .committee-title { font-weight: bold; margin-bottom: 15px; text-align: center; }
  .committee-member { margin: 8px 0; display: flex; justify-content: space-between; align-items: center; }
  .signature-line { border-bottom: 1px solid #000; width: 150px; height: 20px; }
  .footer-section { margin-top: 15px; font-size: 12px; }
  .approval-text { margin: 20px 0; text-align: center; font-size: 13px; }
`;

const COMMITTEE_MEMBER = `
  <div class="committee-member">
    <span>الدكتور(ة): _________________________________ الصفة: _____________</span>
    <div class="signature-line"></div>
  </div>`;

export const buildProcesVerbal = (p) => {
  const birthDate = fmt(p.birth_date);
  const birthPlace = esc(p.birth_place);
  const birth = birthDate && birthPlace ? `${birthDate} بـ ${birthPlace}`
    : birthDate || (birthPlace ? `بـ ${birthPlace}` : '');
  const origin = window.location.origin;

  return `<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
  <meta charset="UTF-8">
  <title>محضر مناقشة - ${esc(p.full_name)}</title>
  <style>${STYLE}</style>
</head>
<body>
  <div class="header">
    <div class="logo-container"><img src="${origin}/masterform/logo.png" alt="Logo"></div>
    <div class="right-logo-container"><img src="${origin}/masterform/UH1.png" alt="UH1 Logo"></div>
  </div>

  <div class="title">محضر مناقشة بحث لنيل شهادة الماستر</div>

  <div class="info-section">
    <div class="info-line">
      <span class="label">الاسم الشخصي والعائلي:</span>
      <span class="value">${esc(p.full_name)}</span>
    </div>
    <div class="info-line">
      <span class="label">CIN:</span>
      <span class="value">${esc(p.national_card_id)}</span>
      <span class="label" style="margin-right: 50px;">CNE/CODE MASSAR:</span>
      <span class="value">${esc(p.national_student_id)}</span>
    </div>
    <div class="info-line">
      <span class="label">N°APOGEE:</span>
      <span class="value">${esc(p.apogee_number)}</span>
    </div>

    <div class="signature-box">
      <div class="signature-title">توقيع الادارة</div>
      <div class="signature-frame"></div>
    </div>

    <div class="info-line">
      <span class="label">تاريخ ومكان الإزدياد:</span>
      <span class="value">${birth}</span>
    </div>
    <div class="info-line">
      <span class="label">تاريخ أول تسجيل بالماستر:</span>
      <span class="value">${esc(p.registration_date)}</span>
    </div>
    <div class="info-line">
      <span class="label">الإجازة المحصل عليها:</span>
      <span class="value">${esc(p.license_obtained)}</span>
    </div>
    <div class="info-line">
      <span class="label">تخصص الماستر:</span>
      <span class="value">${esc(p.master_name)}</span>
    </div>
  </div>

  <div class="research-title">
    <div style="margin-bottom: 10px; font-weight: bold;">عنوان البحث:</div>
    <div class="title-line">${esc(p.research_title)}</div>
    <div class="title-line"></div>
    <div class="title-line"></div>
  </div>

  <div class="info-line">
    <span class="label">تحت إشراف الدكتور(ة):</span>
    <span class="value">${esc(p.supervisor_name)}</span>
  </div>

  <div class="committee-section">
    <div class="info-line" style="text-align: center; margin: 12px 0; font-size: 14px;">
      <span>نوقش يوم</span>
      <span class="value" style="min-width: 150px;">_______________</span>
      <span>بمقر الكلية على الساعة</span>
      <span class="empty-field" style="min-width: 100px;"></span>
      <span>للحصول على شهادة الماستر</span>
    </div>
    <div class="committee-title">أعضاء لجنة المناقشة تتكون من:</div>
    ${COMMITTEE_MEMBER.repeat(4)}
  </div>

  <div class="footer-section">
    <div class="approval-text">
      بعد الاستماع إلى العرض والتوضيحات والملاحظات التي قدمها أعضاء لجنة المناقشة انسحب الحضور من أجل المداولة
    </div>
    <div class="info-line" style="text-align: center; margin: 12px 0; font-size: 14px;">
      <span>ثم قبول بالبحث بنقطة:</span>
      <span class="value2" style="min-width: 80px;">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
      <span>-بالأرقام :</span>
      <span class="value" style="min-width: 50px;">____</span>
      <span>/20</span>
      <span style="margin: 0 10px;">-بالأحرف:</span>
      <span class="value" style="min-width: 150px;">_______________</span>
      <span>/عشرون</span>
    </div>
  </div>
  <script>
    // Wait for the logos before printing, like the PHP version's onload + 500 ms
    window.onload = function () { setTimeout(function () { window.print(); }, 500); };
  </script>
</body>
</html>`;
};

// Must be called directly from a click handler so the popup isn't blocked.
export const printProcesVerbal = (participant) => {
  const win = window.open('', '_blank');
  if (!win) return false;
  win.document.open();
  win.document.write(buildProcesVerbal(participant));
  win.document.close();
  return true;
};
